/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import { DateRange, delay, guid, UTC } from 'rewire-common';
import { freeze, observable } from 'rewire-core';
import { createColumn, createGrid, ICell, ICellCustomValue, IColumn, IGrid, IGridOptions, IRowData } from 'rewire-grid';
import {
  getMultipleObject,
  IArea,
  IEmployee,
  IGenerator,
  IGroup,
  IJob,
  IOccupation,
  IReason,
  ITimeOffRange,
  ITimeOffRequest,
  ScheduleFilterInput,
} from '../DataTypes';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import { TimeOffRouteManagerBase, timeOffCalendarRouteManager, timeOffListRouteManager } from '../main-navigation/RouteManager';
import { getUdfDisplayValue } from '../schedule/UdfsModel';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
// import PagingModel                      from '../settings/PagingModel';
import mainDateRangeNavModel from '../ui/MainDateRangeNavModel';
import { formatTime, YMDFormat } from '../utility/DateUtils';
import { nameObjCompare, nameObjMap } from '../utility/gridUtils';
import SelectedCellWatcher from '../utility/SelectedCellWatcher';
import { capitalize } from '../utility/StringUtils';
import infoPanelTimeOffRequestViewModel from './InfoPanelTimeOffRequestViewModel';
import { TimeOffDateCell, TimeOffDefaultCell, TimeOffEmployeeCell, TimeOffJobCell, TimeOffStatusCell } from './TimeOffCells';

export const timeOffRequestReturnSchema = `
  id
  employee {
    id
    name
    fields
  }
  effective
  partialDay
  status
  preference
  comment
  timeOffRanges {
    effective
    units
    reason {
      id
      name
    }
  }
  fields
  createdBy
  createdDate
  job {
    id
    group {
      id
      name
      area {
        id
        name
      }
    }
    occupation {
      id
      name
    }
    generator {
      id
      name
      cycle {
        id
      }
    }
    effective
    cycleOffset
  }
`;

interface IEmployeeTimeOffRequestsRow {
  employee: IEmployee;
  jobs: IJob[];
  timeOffRequestsByDate: (ITimeOffRequest[] | undefined)[];
}

interface ITimeOffRequestRow {
  timeOffRequest: ITimeOffRequest;
  jobs: IJob[];
}

export interface ITimeOffRequestText {
  timeOffRanges: string[];
  status: string;
}

export class TimeOffDateCellData implements ICellCustomValue {
  id: string;
  date: UTC;
  isDayOff: boolean;
  timeOffRequests?: ITimeOffRequest[];
  timeOffRequestsText?: ITimeOffRequestText[];

  constructor(date: UTC, isDayOff: boolean, timeOffRequests?: ITimeOffRequest[]) {
    this.date = date;
    this.isDayOff = isDayOff;
    this.id = guid();
    this.timeOffRequests = timeOffRequests;
    this.timeOffRequestsText = this.timeOffRequests?.length
      ? this.timeOffRequests.map((tor) => {
          return {
            timeOffRanges: tor.timeOffRanges.map((timeOffRange) => this.timeOffRangeToText(timeOffRange, tor!.partialDay)),
            status: tor.status ? capitalize(tor.status) : '',
          };
        })
      : undefined;
  }

  toString(): string {
    if (!this.timeOffRequestsText?.length) {
      return '';
    }

    const torTextStrings = this.timeOffRequestsText.map((torText) => {
      const timeOffRangesStr = torText.timeOffRanges.join('\n');
      const statusStr = torText.status;
      return `${timeOffRangesStr}\n\n${statusStr}`;
    });

    return torTextStrings.join('\n\n************************\n\n');
  }

  timeOffRangeToText(timeOffRange: ITimeOffRange, partialDay: boolean): string {
    const timeRangeText =
      partialDay && timeOffRange.effective
        ? `${formatTime(timeOffRange.effective.start)}-${formatTime(timeOffRange.effective.end)} - `
        : '';
    const unitsText = partialDay ? `${timeOffRange.units} - ` : '';
    const reasonText = timeOffRange.reason.name;
    return `${timeRangeText}${unitsText}${reasonText}`;
  }
}

export interface ITimeOffJobCellText {
  occupation: string;
  generator?: string;
  effectiveStart: string;
  effectiveEnd: string;
}

export class TimeOffJobCellData implements IJob, ICellCustomValue {
  id: string;
  area?: IArea;
  group: IGroup;
  generator?: IGenerator;
  occupation: IOccupation;
  employee?: IEmployee;
  cycleOffset?: number;
  effective: DateRange;
  position: number;
  fields?: Record<string, unknown>;
  text: ITimeOffJobCellText;

  constructor(job: IJob) {
    ({
      id: this.id,
      area: this.area,
      group: this.group,
      generator: this.generator,
      occupation: this.occupation,
      employee: this.employee,
      cycleOffset: this.cycleOffset,
      effective: this.effective,
      position: this.position,
      fields: this.fields,
    } = job);

    this.text = {
      occupation: this.occupation.name,
      generator: this.generator?.name || '',
      effectiveStart: this.effective.startAsString(),
      effectiveEnd: this.effective.endAsString() || '-',
    };
  }

  toString(): string {
    return `${this.text.occupation}\n${this.text.generator ? `${this.text.generator}\n` : ''}${this.text.effectiveStart} to ${this.text.effectiveEnd}`;
  }
}

export type TimeOffCellType = 'Employee' | 'Job' | 'Date' | 'ListViewCell';
export type TimeOffViewMode = 'Calendar' | 'List';

interface ITimeOffModelState {
  timeOffRequests: ITimeOffRequest[];
  grid: IGrid;
  isLoading: boolean;
  selectedCell?: ICell;
  prevSelectedCell?: ICell;
  viewMode: TimeOffViewMode;
  deletedTimeOffIds?: string[];
}

export class TimeOffModel {
  state: ITimeOffModelState;
  // pagingModel:       PagingModel;
  selectedCellWatcher: SelectedCellWatcher = new SelectedCellWatcher();
  private _active: boolean;

  constructor() {
    this.state = observable({
      timeOffRequests: [] as ITimeOffRequest[],
      grid: {} as IGrid,
      isLoading: false,
      viewMode: 'Calendar',
    });
    this._active = false;
    // this.pagingModel = new PagingModel(this._loadGrid);
  }

  activate() {
    if (!this._active) {
      this._active = true;
      infoPanelTimeOffRequestViewModel.onEvent.addListener('delete', this.onDeleteTimeOffRequests);
      infoPanelTimeOffRequestViewModel.onEvent.addListener('updateStatus', this.onUpdateTimeOffRequestStatus);
      infoPanelTimeOffRequestViewModel.onEvent.addListener('save', this.onSaveTimeOffRequests);
    }
  }

  deactivate() {
    if (this._active) {
      this._active = false;
      infoPanelTimeOffRequestViewModel.onEvent.removeListener('delete', this.onDeleteTimeOffRequests);
      infoPanelTimeOffRequestViewModel.onEvent.removeListener('updateStatus', this.onUpdateTimeOffRequestStatus);
      infoPanelTimeOffRequestViewModel.onEvent.removeListener('save', this.onSaveTimeOffRequests);
    }
  }

  reset() {
    freeze(() => {
      this.state.timeOffRequests.set([]);
      this.state.grid = {} as IGrid;
    });
  }

  async load(viewMode?: TimeOffViewMode) {
    this.activate();
    this.state.isLoading = true;
    try {
      if (viewMode) {
        this.state.viewMode = viewMode;
      }
      this.reset();
      await this.loadGrid();
    } finally {
      this.state.isLoading = false;
    }
  }

  loadGrid = async () => {
    // await this.pagingModel.load();
    await this._loadGrid();
  };

  // private _loadGrid = async (): Promise<{cursor?: string, totalCount: number}> => {
  private _loadGrid = async () => {
    const result = await this.getTimeOffRequests();
    this.state.timeOffRequests = [];
    this.state.timeOffRequests = result.timeOffRequests;
    this.createGrid();
    // return {cursor: result.cursor, totalCount: result.totalCount};
  };

  reloadGrid = async (delayMs = 0) => {
    if (delayMs) {
      await delay(delayMs);
    }
    await this.loadGrid();
  };

  private restoreSelection(selectedCell?: ICell) {
    if (selectedCell) {
      const row = this.state.grid.rows.find((r) => r.id === selectedCell?.row?.id);
      if (row) {
        this.state.grid.selectRows([row]);
        const cell = Object.values(row.cells).find((c) => c.column.name === selectedCell.column.name);
        if (cell) this.state.grid.selectCells([cell]);
      }
    }
  }

  reselectSelectedCellsAfterAction = async (action: () => Promise<boolean | undefined>) => {
    const selectedCells = this.grid.selectedCells.slice();
    await action();
    if (selectedCells.length) {
      this.grid.selectCells(selectedCells);
    }
  };

  getFilteredTimeOffRequests(timeOffRequests: ITimeOffRequest[]): ITimeOffRequest[] {
    // TODO: does not currently filter custom filters client side.
    let filteredTimeOffRequests = timeOffRequests.slice();
    const filters = this.routeManager.filters;
    if (filters) {
      filteredTimeOffRequests = timeOffRequests.filter((t) => {
        if (filters.areaIds) {
          if (!filters.areaIds.some((areaId) => areaId === t.job?.group?.area?.id)) {
            return false;
          }
        }
        if (filters.groupIds) {
          if (!filters.groupIds.some((groupId) => groupId === t.job?.group?.id)) {
            return false;
          }
        }
        if (filters.employeeIds) {
          if (!filters.employeeIds.some((employeeId) => employeeId === t.employee.id)) {
            return false;
          }
        }
        if (filters.occupationIds) {
          if (!filters.occupationIds.some((occupationId) => occupationId === t.job?.occupation?.id)) {
            return false;
          }
        }
        if (filters.generatorIds) {
          if (!filters.generatorIds.some((generatorId) => generatorId === t.job?.generator?.id)) {
            return false;
          }
        }
        if (filters.reasonIds) {
          if (!filters.reasonIds.some((reasonId) => t.timeOffRanges.some((timeOffRange) => timeOffRange.reason.id === reasonId))) {
            return false;
          }
        }

        return true;
      });
    }

    return filteredTimeOffRequests;
  }

  onSaveTimeOffRequests = (timeOffRequests: ITimeOffRequest[]) => {
    const filteredTimeOffRequests = this.getFilteredTimeOffRequests(timeOffRequests);

    for (const tor of timeOffRequests) {
      const existingTorIdx = this.timeOffRequests.findIndex((t) => t.id === tor.id);
      if (existingTorIdx >= 0) {
        if (filteredTimeOffRequests.some((t) => t.id === tor.id)) {
          // update
          Object.assign(this.timeOffRequests[existingTorIdx], tor);
        } else {
          // remove
          this.timeOffRequests.splice(existingTorIdx, 1);
        }
      } else {
        this.timeOffRequests.push(tor);
      }
    }

    this.updateGrid();
  };

  onUpdateTimeOffRequestStatus = (id: string, status: string) => {
    // there can be multiple time off requests with the same id, as a time off request from the server can be duplicated
    // multiple times for every job they have, in the case of multiple jobs.
    const torsToUpdate = this.timeOffRequests.filter((tor) => tor.id === id);
    if (!torsToUpdate.length) {
      return;
    }

    for (const tor of torsToUpdate) {
      tor.status = status;
    }

    this.updateGrid();
  };

  updateGrid() {
    const selectedCell = this.state.selectedCell;
    const newRows =
      this.viewMode === 'Calendar'
        ? this.createCalendarViewGridRows(this.timeOffRequests, this.grid.columns)
        : this.createListViewGridRows(this.timeOffRequests, this.grid.columns);
    this.selectedCellWatcher.unwatch();
    this.grid.selectedCells.length = 0;
    this.grid.set(newRows);
    this.selectedCellWatcher.watch(this.grid, this.grid.rows, (foundCell) => {
      this.state.prevSelectedCell = this.state.selectedCell;
      this.state.selectedCell = foundCell;
    });
    this.restoreSelection(selectedCell);
  }

  getTimeOffRequests = async (): Promise<{ timeOffRequests: ITimeOffRequest[]; cursor?: string; totalCount: number }> => {
    const returnSchema = timeOffRequestReturnSchema;
    const effective = this.routeManager.effective!;
    const { filters, groups } = this.routeManager;
    let sorts = this.routeManager.sorts;
    const updatedFilters = this.updateFilters(filters);

    if (!sorts) sorts = {};

    // const result          = await DataQueries.getTimeOffRequests(returnSchema, effective, filters, groups?.[0], sorts, this.pagingModel.itemsPerPage, this.pagingModel.currentPage);
    const result = await DataQueries.getTimeOffRequests(returnSchema, effective, updatedFilters, groups, sorts, 1000);
    const timeOffRequests = result?.data as ITimeOffRequest[];
    // if (timeOffRequests?.length) {
    //   this.sortTimeOffRequestTimeOffRanges(timeOffRequests);
    // }
    return { timeOffRequests: timeOffRequests, cursor: result.cursor, totalCount: result.totalCount };
  };

  updateFilters(originalFilters: ScheduleFilterInput | undefined): ScheduleFilterInput | undefined {
    if (!originalFilters) return undefined;
    if (!originalFilters.timeOffStatus) return originalFilters;

    const filters: ScheduleFilterInput = Object.assign({}, originalFilters);
    if (!filters.custom) filters.custom = [];
    filters.custom.push({
      field: 'status',
      eq: filters.timeOffStatus,
    });
    delete filters.timeOffStatus;
    return filters;
  }

  // sortTimeOffRequestTimeOffRanges(timeOffRequests: ITimeOffRequest[]) {
  //   timeOffRequests.forEach(timeOffRequest => {
  //     this.sortTimeOffRanges(timeOffRequest.timeOffRanges);
  //   });
  // }

  // sortTimeOffRanges = async (timeOffRanges: ITimeOffRange[]) => {
  //   timeOffRanges.sort((timeOffRange1: ITimeOffRange, timeOffRange2: ITimeOffRange) => {
  //     const startTime1    = timeOffRange1.effective?.start?.date;
  //     const startTime2    = timeOffRange2.effective?.start?.date;
  //     const hasStartTime1 = !isNullOrUndefined(startTime1);
  //     const hasStartTime2 = !isNullOrUndefined(startTime2);
  //     let sortResult      = !hasStartTime1 && hasStartTime2 ? -1 : hasStartTime1 && !hasStartTime2 ? 1 : !hasStartTime1 && !hasStartTime2 ? 0 : startTime1! < startTime2! ? -1 : startTime1! > startTime2! ? 1 : 0;
  //     if (sortResult === 0) {
  //       let endTime1    = timeOffRange1.effective?.end?.date;
  //       let endTime2    = timeOffRange2.effective?.end?.date;
  //       let hasEndTime1 = !isNullOrUndefined(endTime1);
  //       let hasEndTime2 = !isNullOrUndefined(endTime2);
  //       sortResult      = !hasEndTime1 && hasEndTime2 ? -1 : hasEndTime1 && !hasEndTime2 ? 1 : !hasEndTime1 && !hasEndTime2 ? 0 : endTime1! < endTime2! ? -1 : endTime1! > endTime2! ? 1 : 0;
  //     }
  //     if (sortResult === 0) {
  //       let reason1 = timeOffRange1.reason;
  //       let reason2 = timeOffRange2.reason;
  //       sortResult  = reason1!.id.localeCompare(reason2!.id);
  //     }
  //     if (sortResult === 0) {
  //       let units1 = timeOffRange1.units;
  //       let units2 = timeOffRange2.units;
  //       sortResult = units1! < units2! ? -1 : units1! > units2! ? 1 : 0;
  //     }
  //     return sortResult;
  //   });
  // }

  get timeOffRequests(): ITimeOffRequest[] {
    return this.state.timeOffRequests;
  }

  get grid(): IGrid {
    return this.state.grid;
  }

  get selectedCells(): ICell[] {
    return this.grid.selectedCells || [];
  }

  get selectedCell(): ICell | undefined {
    return this.selectedCells[0];
  }

  get isMultiSelect(): boolean {
    return this.selectedCells.length > 1;
  }

  get isLoading(): boolean {
    // return this.state.isLoading || this.pagingModel.loading || this.routeManager.isLoading;
    return this.state.isLoading || this.routeManager.isLoading;
  }

  get canAddTimeOffRequest(): boolean {
    return mainPageModel.canAddTimeOffRequests;
  }

  get canEditTimeOffRequest(): boolean {
    return mainPageModel.canEditTimeOffRequests;
  }

  get canDeleteTimeOffRequest(): boolean {
    return mainPageModel.canDeleteTimeOffRequests;
  }

  get canViewTimeOffBalances(): boolean {
    return mainPageModel.canViewTimeOffBalances;
  }

  get hasResults(): boolean {
    return !!this.timeOffRequests?.length;
  }

  get hasRows(): boolean {
    return !!this.grid?.rows?.length;
  }

  get viewMode(): TimeOffViewMode {
    return this.state.viewMode;
  }

  get routeManager(): TimeOffRouteManagerBase {
    return this.viewMode === 'Calendar' ? timeOffCalendarRouteManager : timeOffListRouteManager;
  }

  getEmployeeUdfNames(): string[] {
    return udfsSubscriptionService.subscriptionModel.getUdfFieldNames(['employees']);
  }

  isViewMode(viewMode: TimeOffViewMode): boolean {
    return this.viewMode === viewMode;
  }

  getCellType(cell: ICell): TimeOffCellType {
    if (this.viewMode === 'List') {
      return 'ListViewCell';
    }

    if (cell.column.name === 'employee') {
      return 'Employee';
    }
    if (cell.column.name === 'job') {
      return 'Job';
    }
    return 'Date';
  }

  getDateIdx(idx: number) {
    return idx - 2;
  }

  getDateCellDate(cell: ICell): UTC {
    const idx = this.getDateIdx(cell.columnPosition);
    return mainDateRangeNavModel.startDate.add(idx);
  }

  selectedCellEqualsPrevSelectedCell(): boolean {
    return (
      this.state.selectedCell?.row.id === this.state.prevSelectedCell?.row.id &&
      this.state.selectedCell?.column.name === this.state.prevSelectedCell?.column.name
    );
  }

  async addTimeOffRequest() {
    if (!this.canAddTimeOffRequest) return;
    const selectedCell = this.selectedCell;
    this.grid?.clearSelection?.();
    await infoPanelTimeOffRequestViewModel.load(undefined, true, selectedCell);
  }

  onDeleteTimeOffRequests = (timeOffIds: string[]) => {
    if (!this.canDeleteTimeOffRequest) return;
    this.state.timeOffRequests = this.timeOffRequests.filter((tor) => !timeOffIds.some((torId) => torId === tor.id));
    this.updateGrid();
  };

  toDisplayMonthString(utc?: UTC): string {
    if (!utc) return '';
    const date = utc.toDate();
    const monthShort = date.toLocaleDateString('en-us', { month: 'short', timeZone: 'UTC' });
    return `${monthShort}`;
  }

  toDisplayDateString(utc?: UTC): string {
    if (!utc) return '';
    const date = new Date(utc.utc);
    const dayNum = date.getUTCDate();
    return `${dayNum}`;
  }

  canSelectCellFn = (cell: ICell) => {
    const grid = cell.grid;
    if (!grid.selectedCells.length || !grid.startCell) {
      return true;
    }
    const cellToSelectType = this.getCellType(cell);
    if (cellToSelectType !== 'Date') {
      return false;
    }
    return grid.selectedCells.every((c: ICell) => this.getCellType(c) === 'Date' && c.row.id === cell.row.id);
  };

  calculateIfDateIsDayOff(date: UTC, jobs: IJob[]): boolean {
    if (!jobs.length) {
      return false;
    }

    for (const job of jobs) {
      const cycleIdx = (date.subtractDate(job.effective.start) + (job.cycleOffset ?? 0)) % (job.generator?.cycle.length ?? 1);
      const jobTemplate = job.generator?.cycle[cycleIdx];
      if (jobTemplate?.id) {
        return false;
      }
    }

    return true;
  }

  createGrid() {
    const selectedCell = this.state.selectedCell;
    this.selectedCellWatcher.unwatch();
    this.state.grid = this.viewMode === 'Calendar' ? this.createCalendarViewGrid() : this.createListViewGrid();
    this.selectedCellWatcher.watch(this.grid, this.grid.rows, (foundCell) => {
      this.state.prevSelectedCell = this.state.selectedCell;
      this.state.selectedCell = foundCell;
    });
    this.restoreSelection(selectedCell);
  }

  createCalendarViewGrid(): IGrid {
    const cols: IColumn[] = observable([]);
    const gridGroupBy: string[] = [];
    const employeeWidth = '175px';
    const jobWidth = '175px';
    const dateWidth = '85px';

    cols.push(
      createColumn('employee', 'Employee', {
        type: 'none',
        width: employeeWidth,
        align: 'left',
        fixed: true,
        canSort: false,
        map: nameObjMap,
        compare: nameObjCompare,
        renderer: TimeOffEmployeeCell,
      }),
    );
    cols.push(
      createColumn('job', 'Assigned Job', {
        type: 'none',
        width: jobWidth,
        align: 'left',
        fixed: true,
        canSort: false,
        renderer: TimeOffJobCell,
      }),
    );
    for (let col = 2; col < mainDateRangeNavModel.numOfDays + 2; col++) {
      cols.push(
        createColumn(`date${col - 2}`, this.toDisplayDateString(mainDateRangeNavModel.startDate.add(col - 2)), {
          type: 'none',
          width: dateWidth,
          align: 'center',
          canSort: false,
          verticalAlign: 'top',
          renderer: TimeOffDateCell,
        }),
      );
    }

    if (this.routeManager.groups) {
      for (const group of this.routeManager.groups) {
        gridGroupBy.push(group);
        if (cols.some((col: IColumn) => col.name === group)) continue;
        cols.push(
          createColumn(`${group}`, capitalize(group), {
            type: 'none',
            canSort: false,
            map: nameObjMap,
            compare: nameObjCompare,
          }),
        );
      }
    }

    const gridVariableKeybinds = {
      'Ctrl+Insert': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      'Ctrl+D': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      'Ctrl+Delete': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      Delete: (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      'Ctrl+X': async (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      'Ctrl+V': async (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
    };

    const rows = this.createCalendarViewGridRows(this.timeOffRequests, cols);

    // create the grid model
    const gridOptions: IGridOptions = {
      clearSelectionOnBlur: false,
      variableKeybinds: gridVariableKeybinds,
      groupBy: gridGroupBy,
      multiSelect: false,
      // multiSelect:             true,
      // canSelectCellFn:         this.canSelectCellFn,
    };
    const grid: IGrid = createGrid(rows, cols, gridOptions);

    const dateHeaderData: Record<string, string> = {};
    const startDate = mainDateRangeNavModel.startDateStartOfDay;
    let currMonthStr = this.toDisplayMonthString(startDate);
    let daysOfMonthShown = 0;
    const monthDayFrequency = 6;
    for (let i = 0; i < mainDateRangeNavModel.numOfDays; i++) {
      const newMonthStr = this.toDisplayMonthString(startDate.add(i));
      const isNewMonth = newMonthStr.localeCompare(currMonthStr) !== 0;

      if (daysOfMonthShown < monthDayFrequency || isNewMonth) {
        // if new month or it is a new week, set the month in the header
        dateHeaderData[`date${i}`] = newMonthStr;
      }

      currMonthStr = newMonthStr;

      if (daysOfMonthShown >= monthDayFrequency || isNewMonth) {
        daysOfMonthShown = 0;
      } else {
        daysOfMonthShown++;
      }
    }
    grid.addFixedRow({ data: dateHeaderData }, 0);

    return grid;
  }

  createCalendarViewGridRows(timeOffRequests: ITimeOffRequest[], cols: IColumn[]): IRowData[] {
    const dates: UTC[] = [];
    const timeOffRequestsByEmployee: Record<string, IEmployeeTimeOffRequestsRow> = {};

    for (let i = 0; i < mainDateRangeNavModel.numOfDays; i++) {
      dates[i] = mainDateRangeNavModel.startDateStartOfDay.add(i);
    }

    for (const tor of timeOffRequests) {
      const employeeId = tor.employee!.id!;
      if (Object.prototype.hasOwnProperty.call(timeOffRequestsByEmployee, employeeId)) {
        if (
          tor.job &&
          (!timeOffRequestsByEmployee[employeeId].jobs.length ||
            !timeOffRequestsByEmployee[employeeId].jobs.some((j: IJob) => j.id === tor.job?.id))
        ) {
          timeOffRequestsByEmployee[employeeId].jobs.push(tor.job);
        }
        for (let i = 0; i < mainDateRangeNavModel.numOfDays; i++) {
          if (tor.effective.inRange(dates[i])) {
            if (!timeOffRequestsByEmployee[employeeId].timeOffRequestsByDate[i]?.some((t: ITimeOffRequest) => t.id === tor.id)) {
              if (timeOffRequestsByEmployee[employeeId].timeOffRequestsByDate[i]) {
                (timeOffRequestsByEmployee[employeeId].timeOffRequestsByDate[i] as any).push(tor);
              } else {
                timeOffRequestsByEmployee[employeeId].timeOffRequestsByDate[i] = [tor];
              }
            }
          }
        }
      } else {
        timeOffRequestsByEmployee[employeeId] = {
          employee: tor.employee,
          jobs: tor.job ? [tor.job] : [],
          timeOffRequestsByDate: [],
        } as IEmployeeTimeOffRequestsRow;
        for (let i = 0; i < mainDateRangeNavModel.numOfDays; i++) {
          const torForDate = tor.effective.inRange(dates[i]) ? [tor] : undefined;
          timeOffRequestsByEmployee[employeeId].timeOffRequestsByDate.push(torForDate);
        }
      }
    }

    const employeeRows = Object.values(timeOffRequestsByEmployee) as IEmployeeTimeOffRequestsRow[];
    const employeeUdfNames = this.getEmployeeUdfNames();

    const rows: IRowData[] = [];
    for (let row = 0; row < employeeRows.length; row++) {
      const r: IRowData = { id: `${employeeRows[row].employee.id}`, data: {} };
      for (let column = 0; column < cols.length; column++) {
        const fieldName: string = cols[column].name;
        const isEmployeeUdfCol = employeeUdfNames.includes(fieldName);
        const jobs = employeeRows[row].jobs;
        let v: any;
        if (fieldName === 'job') {
          v = !jobs.length ? undefined : jobs.length === 1 ? new TimeOffJobCellData(jobs[0]) : getMultipleObject();
        } else if (fieldName.includes('date')) {
          const dateIdx = this.getDateIdx(column);
          const currRow = employeeRows[row];
          const currDate = dates[dateIdx];
          const isDayOff = this.calculateIfDateIsDayOff(currDate, currRow.jobs);
          const tors = currRow.timeOffRequestsByDate[dateIdx];
          v = new TimeOffDateCellData(currDate, isDayOff, tors);
        } else if (fieldName.includes('area')) {
          const areas = jobs.reduce((prev: IArea[], curr: IJob) => {
            if (prev.every((a) => a.id !== curr.group.area.id)) {
              prev.push(curr.group.area);
            }
            return prev;
          }, []);
          v = areas.length > 1 ? getMultipleObject() : areas[0];
        } else if (fieldName.includes('group')) {
          const groups = jobs.reduce((prev: IGroup[], curr: IJob) => {
            if (prev.every((a) => a.id !== curr.group.id)) {
              prev.push(curr.group);
            }
            return prev;
          }, []);
          v = groups.length > 1 ? getMultipleObject() : groups[0];
        } else if (fieldName.includes('occupation')) {
          const occupations = jobs.reduce((prev: IOccupation[], curr: IJob) => {
            if (prev.every((a) => a.id !== curr.occupation.id)) {
              prev.push(curr.occupation);
            }
            return prev;
          }, []);
          v = occupations.length > 1 ? getMultipleObject() : occupations[0];
        } else if (fieldName.includes('generator')) {
          const generators = jobs.reduce((prev: IGenerator[], curr: IJob) => {
            if (curr.generator && prev.every((a) => a.id !== curr.generator?.id)) {
              prev.push(curr.generator);
            }
            return prev;
          }, []);
          v = generators.length > 1 ? getMultipleObject() : generators[0];
        } else if (isEmployeeUdfCol) {
          v = getUdfDisplayValue(fieldName, employeeRows[row].employee?.fields?.[fieldName]);
        } else {
          v = employeeRows[row][fieldName as keyof IEmployeeTimeOffRequestsRow];
        }
        r.data![fieldName] = v;
      }
      rows.push(r);
    }
    return rows;
  }

  createListViewGrid(): IGrid {
    const cols: IColumn[] = observable([]);
    const gridGroupBy: string[] = [];
    const nameObjArrayMap = (value: any) => (value?.length ? value.map((v: any) => v.name).join(', ') : '');
    const employeeWidth = '175px';
    const jobWidth = '175px';
    const columnMinWidth = '80px';

    cols.push(
      createColumn('employee', 'Employee', {
        type: 'none',
        width: employeeWidth,
        align: 'left',
        fixed: true,
        canSort: false,
        map: nameObjMap,
        compare: nameObjCompare,
        renderer: TimeOffEmployeeCell,
      }),
    );
    cols.push(
      createColumn('job', 'Assigned Job', {
        type: 'none',
        width: jobWidth,
        align: 'left',
        fixed: true,
        canSort: false,
        renderer: TimeOffJobCell,
      }),
    );
    cols.push(
      createColumn('startDate', 'Start Date', {
        type: 'text',
        width: columnMinWidth,
        align: 'left',
        canSort: false,
        map: YMDFormat,
        renderer: TimeOffDefaultCell,
      }),
    );
    cols.push(
      createColumn('endDate', 'End Date', {
        type: 'text',
        width: columnMinWidth,
        align: 'left',
        canSort: false,
        map: YMDFormat,
        renderer: TimeOffDefaultCell,
      }),
    );
    cols.push(
      createColumn('startTime', 'Start Time', {
        type: { type: 'time', options: { map: formatTime } },
        width: columnMinWidth,
        align: 'left',
        canSort: false,
        renderer: TimeOffDefaultCell,
      }),
    );
    cols.push(
      createColumn('endTime', 'End Time', {
        type: { type: 'time', options: { map: formatTime } },
        width: columnMinWidth,
        align: 'left',
        canSort: false,
        renderer: TimeOffDefaultCell,
      }),
    );
    cols.push(
      createColumn('units', 'Units', {
        type: { type: 'number', options: { decimals: 2, thousandSeparator: false, allowNegative: false } },
        width: columnMinWidth,
        align: 'left',
        canSort: false,
        renderer: TimeOffDefaultCell,
      }),
    );
    cols.push(
      createColumn('reason', 'Reason(s)', {
        type: 'none',
        width: columnMinWidth,
        align: 'left',
        canSort: false,
        map: nameObjArrayMap,
        compare: nameObjCompare,
        renderer: TimeOffDefaultCell,
      }),
    );
    cols.push(
      createColumn('preference', 'Preference', {
        type: { type: 'number' },
        width: columnMinWidth,
        align: 'left',
        canSort: false,
        renderer: TimeOffDefaultCell,
      }),
    );
    cols.push(
      createColumn('status', 'Status', {
        type: 'text',
        width: columnMinWidth,
        align: 'left',
        canSort: false,
        renderer: TimeOffStatusCell,
      }),
    );
    cols.push(
      createColumn('comment', 'Comment', {
        width: columnMinWidth,
        type: 'text',
        align: 'left',
        canSort: false,
        renderer: TimeOffDefaultCell,
      }),
    );
    cols.push(
      createColumn('createdDate', 'Created Date', {
        type: 'text',
        width: columnMinWidth,
        align: 'left',
        canSort: false,
        map: YMDFormat,
        renderer: TimeOffDefaultCell,
      }),
    );
    cols.push(
      createColumn('createdBy', 'Created By', {
        type: 'text',
        width: columnMinWidth,
        align: 'left',
        canSort: false,
        renderer: TimeOffDefaultCell,
      }),
    );

    if (this.routeManager.groups) {
      for (const group of this.routeManager.groups) {
        gridGroupBy.push(group);
        if (cols.some((col: IColumn) => col.name === group)) continue;
        cols.push(
          createColumn(`${group}`, capitalize(group), {
            type: 'none',
            canSort: false,
            map: nameObjMap,
            compare: nameObjCompare,
          }),
        );
      }
    }

    const gridVariableKeybinds = {
      'Ctrl+Insert': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      'Ctrl+D': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      'Ctrl+Delete': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      Delete: (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      'Ctrl+X': async (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      'Ctrl+V': async (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
    };

    const rows = this.createListViewGridRows(this.timeOffRequests, cols);

    // create the grid model
    const gridOptions: IGridOptions = {
      readOnly: true,
      clearSelectionOnBlur: false,
      variableKeybinds: gridVariableKeybinds,
      groupBy: gridGroupBy,
      multiSelect: false,
      // multiSelect:             true,
      // canSelectCellFn:         this.canSelectCellFn,
    };

    const grid: IGrid = createGrid(rows, cols, gridOptions);
    return grid;
  }

  createListViewGridRows(timeOffRequests: ITimeOffRequest[], cols: IColumn[]): IRowData[] {
    const uniqueTimeOffRequests: Record<string, ITimeOffRequestRow> = {};

    for (const tor of timeOffRequests) {
      if (Object.prototype.hasOwnProperty.call(uniqueTimeOffRequests, tor.id)) {
        if (
          tor.job &&
          (!uniqueTimeOffRequests[tor.id].jobs.length ||
            !uniqueTimeOffRequests[tor.id].jobs.some((j: IJob) => j.id === tor.job?.id))
        ) {
          uniqueTimeOffRequests[tor.id].jobs.push(tor.job);
        }
      } else {
        uniqueTimeOffRequests[tor.id] = {
          timeOffRequest: tor,
          jobs: tor.job ? [tor.job] : [],
        } as ITimeOffRequestRow;
      }
    }

    const timeOffRequestRows = Object.values(uniqueTimeOffRequests) as ITimeOffRequestRow[];

    const rows: IRowData[] = [];
    const employeeUdfNames = this.getEmployeeUdfNames();
    for (const timeOffRequestRow of timeOffRequestRows) {
      const timeOffRequest = timeOffRequestRow.timeOffRequest;
      const r: IRowData = { id: `${timeOffRequest.id}`, data: {} };
      let startTime: UTC | undefined = undefined;
      let endTime: UTC | undefined = undefined;
      let units = 0;
      const reasons: IReason[] = [];
      const jobs = timeOffRequestRow.jobs;
      for (const timeOffRange of timeOffRequest.timeOffRanges) {
        units += timeOffRange.units;
        if (timeOffRange.effective) {
          if (!startTime || timeOffRange.effective.start < startTime) {
            startTime = timeOffRange.effective.start;
          }
          if (!endTime || timeOffRange.effective.end > endTime) {
            endTime = timeOffRange.effective.end;
          }
        }
        if (!reasons.some((r) => r.id === timeOffRange.reason.id)) {
          reasons.push(timeOffRange.reason);
        }
      }

      for (let column = 0; column < cols.length; column++) {
        const fieldName: string = cols[column].name;
        const isEmployeeUdfCol = employeeUdfNames.includes(fieldName);
        let v: any;
        if (fieldName === 'job') {
          v = !jobs.length ? undefined : jobs.length === 1 ? new TimeOffJobCellData(jobs[0]) : getMultipleObject();
        } else if (fieldName === 'startDate') {
          v = timeOffRequest.effective.start;
        } else if (fieldName === 'endDate') {
          v = timeOffRequest.effective.end;
        } else if (fieldName === 'startTime') {
          v = startTime;
        } else if (fieldName === 'endTime') {
          v = endTime;
        } else if (fieldName === 'units') {
          v = units;
        } else if (fieldName === 'reason') {
          v = reasons;
        } else if (fieldName.includes('area')) {
          const areas = jobs.reduce((prev: IArea[], curr: IJob) => {
            if (prev.every((a) => a.id !== curr.group.area.id)) {
              prev.push(curr.group.area);
            }
            return prev;
          }, []);
          v = areas.length > 1 ? getMultipleObject() : areas[0];
        } else if (fieldName.includes('group')) {
          const groups = jobs.reduce((prev: IGroup[], curr: IJob) => {
            if (prev.every((a) => a.id !== curr.group.id)) {
              prev.push(curr.group);
            }
            return prev;
          }, []);
          v = groups.length > 1 ? getMultipleObject() : groups[0];
        } else if (fieldName.includes('occupation')) {
          const occupations = jobs.reduce((prev: IOccupation[], curr: IJob) => {
            if (prev.every((a) => a.id !== curr.occupation.id)) {
              prev.push(curr.occupation);
            }
            return prev;
          }, []);
          v = occupations.length > 1 ? getMultipleObject() : occupations[0];
        } else if (fieldName.includes('generator')) {
          const generators = jobs.reduce((prev: IGenerator[], curr: IJob) => {
            if (curr.generator && prev.every((a) => a.id !== curr.generator?.id)) {
              prev.push(curr.generator);
            }
            return prev;
          }, []);
          v = generators.length > 1 ? getMultipleObject() : generators[0];
        } else if (isEmployeeUdfCol) {
          v = getUdfDisplayValue(fieldName, timeOffRequest.employee?.fields?.[fieldName]);
        } else {
          v = timeOffRequest[fieldName as keyof ITimeOffRequest];
        }
        r.data![fieldName] = v;
      }
      rows.push(r);
    }
    return rows;
  }
}

const timeOffModel = new TimeOffModel();
export default timeOffModel;
